import React, { useState, useCallback, useRef } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { TalentExperienceData } from "@/cards/common/types";
import useThemeStore from "@/shared/stores/useThemeStore";
import ImageGridModal from "./ImageGridModal";

interface ImageGridDisplayProps {
  data?: TalentExperienceData;
}

const ImageGridDisplay: React.FC<ImageGridDisplayProps> = ({ data = {} }) => {
  const { darkMode } = useThemeStore();
  // All hooks must be called unconditionally at the top level
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  // Tracks which image is shown in the main carousel display
  const [currentDisplayIndex, setCurrentDisplayIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const gridImages = data.imageGrid?.images || [];

  const goPrev = useCallback(() => {
    if (gridImages.length === 0) return;
    // If modal is open, navigate inside modal; otherwise navigate main display
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(
        selectedImageIndex === 0 ? gridImages.length - 1 : selectedImageIndex - 1
      );
    } else {
      setCurrentDisplayIndex((prev) => (prev === 0 ? gridImages.length - 1 : prev - 1));
    }
  }, [selectedImageIndex, gridImages.length]);

  const goNext = useCallback(() => {
    if (gridImages.length === 0) return;
    // If modal is open, navigate inside modal; otherwise navigate main display
    if (selectedImageIndex !== null) {
      setSelectedImageIndex(
        selectedImageIndex === gridImages.length - 1 ? 0 : selectedImageIndex + 1
      );
    } else {
      setCurrentDisplayIndex((prev) => (prev === gridImages.length - 1 ? 0 : prev + 1));
    }
  }, [selectedImageIndex, gridImages.length]);

  const handleImageClick = useCallback((index: number) => {
    setSelectedImageIndex(index);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedImageIndex(null);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const delta = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(delta) > 40) {
        delta > 0 ? goPrev() : goNext();
      }
      touchStartX.current = null;
    },
    [goPrev, goNext]
  );

  // Early return after all hooks
  if (!data.imageGrid) return null;

  const textSecondary = darkMode ? "var(--neutral-300)" : "var(--neutral-600)";

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: 2,
          gap: 2,
        }}
      >
        {gridImages.length > 0 ? (
          <>
            {/* Main large image display */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: { xs: 260, sm: 360, md: 420 },
                maxHeight: { xs: 260, sm: 360, md: 420 },
                borderRadius: 2,
                overflow: "hidden",
                border: darkMode
                  ? "1px solid rgba(255,255,255,0.06)"
                  : "1px solid rgba(0,0,0,0.06)",
                background: darkMode ? "rgba(0,0,0,0.35)" : "rgba(15,23,42,0.04)",
                boxShadow: darkMode
                  ? "0 10px 28px rgba(0,0,0,0.35)"
                  : "0 8px 20px rgba(15,23,42,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={() => handleImageClick(currentDisplayIndex)}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <Box
                component="img"
                src={gridImages[currentDisplayIndex]}
                alt="Main image"
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
              {gridImages.length > 1 && (
                <>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      goPrev();
                    }}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 8,
                      transform: "translateY(-50%)",
                      background: darkMode ? "rgba(0,0,0,0.5)" : "rgba(15,23,42,0.08)",
                      color: darkMode ? "#fff" : "#0f172a",
                      "&:hover": {
                        background: darkMode ? "rgba(0,0,0,0.65)" : "rgba(15,23,42,0.12)",
                      },
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      goNext();
                    }}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 8,
                      transform: "translateY(-50%)",
                      background: darkMode ? "rgba(0,0,0,0.5)" : "rgba(15,23,42,0.08)",
                      color: darkMode ? "#fff" : "#0f172a",
                      "&:hover": {
                        background: darkMode ? "rgba(0,0,0,0.65)" : "rgba(15,23,42,0.12)",
                      },
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Thumbnail strip */}
            {gridImages.length > 1 && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  overflowX: "auto",
                  pb: 1,
                  "&::-webkit-scrollbar": { height: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    background: darkMode ? "var(--neutral-700)" : "#ccc",
                    borderRadius: 4,
                  },
                }}
              >
                {gridImages.map((img: string, index: number) => (
                  <Box
                    key={index}
                    component="img"
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    onClick={() => {
                      setCurrentDisplayIndex(index);
                    }}
                    sx={{
                      height: 80,
                      width: 80,
                      borderRadius: 1,
                      flexShrink: 0,
                      objectFit: "cover",
                      cursor: "pointer",
                      border:
                        index === currentDisplayIndex
                          ? `2px solid ${darkMode ? "var(--primary-500)" : "var(--primary-600)"}`
                          : `1px solid ${darkMode ? "var(--neutral-700)" : "var(--neutral-300)"}`,
                      transition: "transform 0.2s ease-in-out, border 0.2s ease-in-out",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              width: "100%",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: textSecondary,
            }}
          >
            <Typography variant="body2">No images available</Typography>
          </Box>
        )}
        {data.imageGrid.description && (
          <Typography
            variant="body2"
            sx={{
              color: textSecondary,
              textAlign: "center",
            }}
          >
            {data.imageGrid.description}
          </Typography>
        )}
      </Box>

      {/* Zoom Modal */}
      <ImageGridModal
        open={selectedImageIndex !== null}
        onClose={handleCloseModal}
        images={gridImages}
        selectedIndex={selectedImageIndex}
        onPrev={goPrev}
        onNext={goNext}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />
    </>
  );
};

export default ImageGridDisplay;
